import type { HelpPilotAnswer, HelpView, PilotLine } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Sparkles, ThumbsUp } from 'lucide-react';
import { useEffect } from 'react';
import { ApiError, api } from '../../lib/api';
import { supportUi } from '../../lib/support';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';

/**
 * «Сообщить» → «Нужна помощь»: сначала отвечает Pilot (БЗ-21). Вопрос — текст из поля панели; ответ — по инструкции
 * страницы и статьям базы знаний в пределах прав человека. «Помогло» закрывает вопрос без специалиста («решено Pilot»,
 * БЗ-23); если нет — обычная кнопка панели отправляет обращение, и переписка с Pilot уходит вместе с ним (БЗ-22).
 * Без подключённой модели или права на ИИ-помощника блока нет: вопрос сразу идёт специалисту.
 */
/** Длина вопроса к Pilot (`HelpPilotAskInput`); в обращение уходит весь текст поля */
const MAX = 1000;

export function ReportPilot({
  question,
  path,
  onTranscript,
  onResolved,
}: {
  question: string;
  path: string | null;
  /** Переписка с Pilot для обращения: пусто — Pilot не спрашивали или вопрос с тех пор изменился */
  onTranscript: (lines: PilotLine[]) => void;
  /** Вопрос решён Pilot: панель очищает черновик */
  onResolved: () => void;
}) {
  const qc = useQueryClient();
  const page = path?.split(/[?#]/)[0] || '/';
  const help = useQuery({
    queryKey: ['support', 'help', page],
    queryFn: () => api<HelpView>('/support/help', { query: { path: page } }),
    staleTime: 5 * 60_000,
  });
  const ask = useMutation({
    mutationFn: (q: string) =>
      api<HelpPilotAnswer>('/support/help/ask', { method: 'POST', body: { question: q, path: page } }),
  });
  const asked = ask.variables;
  const answer = ask.data;
  // Ответ относится к тексту, который сейчас в поле: человек переписал вопрос — старая переписка не прикладывается
  const current = Boolean(answer && asked && asked === question.trim().slice(0, MAX));
  const lines: PilotLine[] =
    answer && asked && current
      ? [
          { role: 'user', text: asked },
          { role: 'pilot', text: answer.text, sig: answer.sig },
        ]
      : [];
  const key = lines.map((l) => l.text).join('\n');
  // biome-ignore lint/correctness/useExhaustiveDependencies: переписка меняется вместе с ответом, ключ — её текст
  useEffect(() => onTranscript(lines), [key]);

  const resolved = useMutation({
    mutationFn: () =>
      api<{ id: string; number: string }>('/support/help/resolved', {
        method: 'POST',
        body: { path: page, transcript: lines },
      }),
    onSuccess: () => {
      toast('Спасибо! Отметили, что Pilot помог');
      onResolved();
      void qc.invalidateQueries({ queryKey: ['support'] });
      supportUi.closeReport();
    },
  });

  if (!help.data?.pilot.available) return null;
  const ready = question.trim().length >= 3;
  const long = question.trim().length > MAX;
  const error = ask.error ?? resolved.error;
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-line bg-surface-2 px-3 py-3">
      {answer ? (
        <div className="flex flex-col gap-1.5" aria-live="polite">
          <p className="text-[12px] font-semibold text-ink-2">Pilot отвечает</p>
          <p className="text-[14px] leading-6 whitespace-pre-line">{answer.text}</p>
          {answer.sources.map((s) => (
            <Link
              key={s.path + s.title}
              to={s.path}
              className="inline-flex min-h-11 items-center text-[13px] text-accent underline md:min-h-0"
            >
              {s.title}
            </Link>
          ))}
          <p className="text-[12px] text-ink-2">
            {answer.label}. Не помогло — отправьте вопрос специалисту ниже.
          </p>
          {answer.answered && current ? (
            <Button
              icon={<ThumbsUp className="size-4" />}
              loading={resolved.isPending}
              onClick={() => resolved.mutate()}
              className="self-start"
            >
              Помогло
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-[13px] text-ink-2">
          Pilot может ответить сразу по инструкциям портала. Если ответ не поможет, отправьте вопрос
          специалисту — переписка уйдёт вместе с ним.
        </p>
      )}
      {!current ? (
        <Button
          icon={<Sparkles className="size-4" />}
          loading={ask.isPending}
          disabled={!ready}
          onClick={() => ask.mutate(question.trim().slice(0, MAX))}
          className="self-start"
        >
          {answer ? 'Спросить Pilot ещё раз' : 'Сначала спросить Pilot'}
        </Button>
      ) : null}
      {long && !current ? (
        <p className="text-[12px] text-ink-2">
          Pilot прочитает первую тысячу знаков вопроса; специалисту уйдёт весь текст.
        </p>
      ) : null}
      {error ? <ErrorBox error={error instanceof ApiError ? error : new Error('Pilot не ответил')} /> : null}
    </div>
  );
}
